import React, { useEffect, useMemo, useState } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { Segment, Header, Form, Divider, Button } from "semantic-ui-react";
import { ControlledRadioGroup, ControlledCheckbox } from "../../../components/ControlledForm";
import { PermitsFormField } from "../types/PermitsFormField";
import identity from "../../../selectors/identity";
import { putContact } from "../../../queries/contacts";
import { useMutation } from "react-query";
import network from "../../../selectors/network";
import queryString from "query-string";
import { FormField } from "./FormSection";

import "./ApplicantFormSection.scss";
import { PermitsContact } from "../types/PermitsContact";
import { parseRubyHash } from "../../../utils/functions";
import { ControlledContactInfo } from "../../contacts/components/ControlledContactInfo";
import _ from "lodash";
import { buildUpdateContactPayload } from "../../contacts/utils/buildContactPayload";
import { ContactCard } from "./ContactCard";

interface IPermitsApplicantFormSectionProps {
  section: {
    attributes: {
      description: string;
    };
    fields: PermitsFormField[];
  };
  permitsFormFields: { [id: number]: PermitsFormField };
  readonly?: boolean;
  stacked?: boolean;
  onNext?: () => void;
  hasMore?: boolean;
  applicantOwnerRadio?: number | null;
  setApplicantOwnerRadio?: any;
  addressState?: string;

  isPrintView?: boolean;
  permitsCustomFields?: any;
  isApplyingScreen?: boolean;
  isPublicView?: boolean;
  applicantInfo: PermitsContact;
  applicantExtensionDefId: number | undefined;
  type?: string;
  onChangeInnerValue?: () => void;
  addOriginalField?: React.Dispatch<any>;
  fieldTypesToForceReadonly?: number[];
  applicant: {
    data: {
      attributes: {
        "first-name": string;
        "last-name": string;
        "email": string;
        "phone": string;
        "address": string;
        "address2": string;
        "city": string;
        "state": string;
        "zip": string;
        "country-id": string;
        "physical_address"?: string;
        "physical_address2"?: string;
        "physical_city"?: string;
        "physical_state"?: string;
        "physical_zip"?: string;
        "home-address"?: string; // home address is synonymous with physical address- support both cases
        "home-address2"?: string;
        "home-city"?: string;
        "home-state"?: string;
        "home-zip"?: string;
        "home-country-id"?: string;
        "same-mail-and-physical-address": string;
        "company-name"?: string;
      };
      id: string;
      type: string;
    };
  } | null;
}

interface IValue {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  licenseNumber?: string;
  companyName?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  physical_address?: string;
  physical_address2?: string;
  physical_city?: string;
  physical_state?: string;
  physical_zip?: string;
  sameMailAndPhysicalAddress?: boolean;
}

interface IPermitExtension {
  value: IValue;
}

interface IPermitsApplicantFormSectionForm {
  isApplicant?: number;
  shouldUpdateProfile?: boolean;
  permits_extensions_attributes?: IPermitExtension[];
}

export const PermitsApplicantFormSection = (props: IPermitsApplicantFormSectionProps) => {
  const {
    section,
    permitsFormFields,
    onNext = () => {},
    applicant,
    hasMore,
    applicantOwnerRadio,
    setApplicantOwnerRadio,
    isApplyingScreen = false,
    readonly,
    applicantInfo,
    isPublicView,
    applicantExtensionDefId,
    isPrintView,
    permitsCustomFields,
    addOriginalField,
    type,
    onChangeInnerValue,
    fieldTypesToForceReadonly,
  } = props;

  if (!applicant) return <></>;

  const applicantInfoField = useMemo(
    () => section.fields.find((field) => field.attributes.isApplicantField),
    [section],
  );
  const contactInfoOptions = useMemo(
    () => parseRubyHash(applicantInfoField?.attributes.contactInfoOptions),
    [applicantInfoField],
  );
  const [resetTriggered, setResetTriggered] = useState(false);
  const [readOnlyValuesTriggered, setReadOnlyValuesTriggered] = useState(false);

  //find applicant information section where isApplicant field is true
  //permitsFormFields is input because it has the same field order as data.permits_extensions_attributes in the onSubmit event

  //field index of applicant information field
  let applicantInfoFieldIndex = _.values(permitsFormFields).findIndex(
    (field: any) => field?.attributes?.isApplicantField === true,
  );

  //if applicant information field is not found, set index to 0
  if (applicantInfoFieldIndex < 0) applicantInfoFieldIndex = 0;

  //store value name of applicant information field
  const applicantInfoFieldName: string = "permits_extensions_attributes." + applicantInfoFieldIndex + ".value";
  const params = queryString.parse(location.search) as any;
  const isStaffSubmission = params.staffSubmission == "true"; // staff submitting for a citizen: autofill fields and don't show radio button options
  const applicationForm = useFormContext();
  const isDefaultApplicant = applicantOwnerRadio !== null ? applicantOwnerRadio : !isStaffSubmission ? -1 : 1;

  const { control, watch } = useForm<IPermitsApplicantFormSectionForm>({
    defaultValues: { isApplicant: isDefaultApplicant, shouldUpdateProfile: true },
  });
  const sectionDescription = section?.attributes.description;

  const fields = applicationForm.watch("permits_extensions_attributes");
  const isCitizen = useSelector(identity.isCitizen);
  const isApplicantField = watch("isApplicant");

  // Maintain the previously clicked radio values if the user goes back and forth between form sections
  useEffect(() => {
    if (isApplicantField !== -1) {
      setApplicantOwnerRadio(isApplicantField);
    }
  }, [isApplicantField]);

  const shouldUpdateProfile = watch("shouldUpdateProfile");
  const showFields = applicantOwnerRadio !== null;
  const networkHeaders = useSelector(network.defaultHeaders);
  const [updateContact] = useMutation(putContact(networkHeaders, applicant.data?.id));

  const isApplicantContactInfoOptions = {
    ...contactInfoOptions,
    first_name: "read_only",
    last_name: "read_only",
    email_address: "read_only",
  };

  const defaultValues = {
    firstName: applicant?.data?.attributes["first-name"],
    lastName: applicant?.data?.attributes["last-name"],
    email: applicant?.data?.attributes.email,
    phone: applicant?.data?.attributes.phone,
    address: applicant?.data?.attributes.address,
    address2: applicant?.data?.attributes.address2,
    city: applicant?.data?.attributes.city,
    state: applicant?.data?.attributes.state,
    zip: applicant?.data?.attributes.zip,
    countryId: applicant?.data?.attributes["country-id"] ?? "US",
    physicalAddress: applicant?.data?.attributes?.["home-address"],
    physicalAddress2: applicant?.data?.attributes?.["home-address2"],
    physicalCity: applicant?.data?.attributes?.["home-city"],
    physicalState: applicant?.data?.attributes?.["home-state"],
    physicalZip: applicant?.data?.attributes?.["home-zip"],
    homeCountryId: applicant?.data?.attributes?.["home-country-id"] ?? "US",
    sameMailAndPhysicalAddress: applicant?.data?.attributes["same-mail-and-physical-address"],
    companyName: applicant?.data?.attributes["company-name"],
  };

  const fieldsList = (section.fields || []).sort((a: any, b: any) => a.attributes.order - b.attributes.order);

  // if this is not the applying screen, display contact card instead of form fields
  if (!isApplyingScreen) {
    return (
      <Form>
        <div className='applicant-form-details-view'>
          <Header as='h3' className='tw-mb-2 tw-font-normal'>
            <Header.Content>Applicant Information</Header.Content>
          </Header>
          <div className='applicant-extra-form-fields'>
            <div className='applicant-field'>
              <ContactCard
                contact={applicantInfo}
                showEditButton={!isPublicView}
                permitsExtensionId={applicantExtensionDefId}
                contactInfoOptions={contactInfoOptions}
                isPrintView={isPrintView}
                isPublicView={isPublicView}
              />
            </div>
            {_.compact(
              fieldsList
                .filter((field) => !field.attributes.isApplicantField)
                .map((field) => (
                  <FormField
                    applicationForm={applicationForm}
                    key={field.id}
                    readonly={readonly}
                    enforceRequired={field.attributes.required}
                    fields={fields}
                    permitsCustomFields={permitsCustomFields}
                    permitsFormField={field}
                    addOriginalField={addOriginalField}
                    type={type}
                    onChangeInnerValue={onChangeInnerValue}
                    isCitizen={isCitizen}
                    person={applicant}
                    isApplyingScreen={isApplyingScreen}
                    isPublicView={isPublicView}
                    isPrintView={isPrintView}
                    fieldTypesToForceReadonly={fieldTypesToForceReadonly}
                    errors={applicationForm.errors}
                  />
                )),
            )}
          </div>
        </div>
      </Form>
    );
  }

  const onSubmit = (data: any, event: any) => {
    event.preventDefault();

    //get contact information from applicant information section of form
    const contactInfo = data.permits_extensions_attributes[applicantInfoFieldIndex].value;

    // Add a null contact_id so that the backend knows to create a new contact_information record
    // Optionally connect the user to the contact_information record
    applicationForm.setValue(applicantInfoFieldName, {
      ...contactInfo,
      userId: isApplicantField ? applicant.data.id : null,
      contactId: null,
      contactType: "Applicant",
      isApplicant: true,
    });

    // Update applicant contact if "update my profile" checkbox is checked
    if (isApplicantField && shouldUpdateProfile && !isStaffSubmission) {
      const updateContactPayload = buildUpdateContactPayload(contactInfo);
      updateContact(updateContactPayload);
    }

    onNext();
  };

  return (
    <div className='applicant-information-section'>
      <Segment basic clearing style={{ marginBottom: 0, paddingBottom: 0 }}>
        <Header as='h2'>Applicant Information</Header>
        <fieldset>
          <legend> {sectionDescription} </legend>
          {!isStaffSubmission && (
            <ControlledRadioGroup
              control={control}
              name='isApplicant'
              options={[
                {
                  key: "isApplicant",
                  value: 1,
                  text: "I am the Applicant",
                  onClick: () => setReadOnlyValuesTriggered(true),
                  ariaLabel: "I am the Applicant",
                },
                {
                  key: "isNotApplicant",
                  value: 0,
                  text: "I am applying on behalf of Someone Else",
                  onClick: () => setResetTriggered(true),
                  ariaLabel: "I am applying on behalf of Someone Else",
                },
              ]}
            />
          )}
        </fieldset>
      </Segment>
      <Form
        onSubmit={applicationForm.handleSubmit(onSubmit)}
        style={{ padding: "8px 16px 24px 16px", display: showFields ? "block" : "none" }}
      >
        <Form.Field style={{ marginBottom: 0, display: applicantInfoField ? "block" : "none" }}>
          <ControlledContactInfo
            key={isApplicantField} // Force re-render when isApplicant changes, so that default values are updated
            formFieldId={applicantInfoField?.id}
            fieldType='contact_info'
            name={applicantInfoFieldName}
            control={applicationForm.control}
            contactRole={"Applicant"}
            isApplicant={isApplicantField == 1}
            readonly={false}
            required={true}
            contactInfoOptions={
              isApplicantField && !isStaffSubmission ? isApplicantContactInfoOptions : contactInfoOptions
            }
            defaultValues={isApplicantField ? defaultValues : {}}
            allowInviteUser={false}
            showDivider={false}
            isApplyingScreen
            displayInputFields
            resetTriggered={resetTriggered}
            readOnlyValuesTriggered={readOnlyValuesTriggered}
            applicantInfoFieldIndex={applicantInfoFieldIndex}
            autofillCountry={!isApplicantField}
          />
        </Form.Field>
        {!!isApplicantField && !isStaffSubmission && (
          <Form.Field>
            <ControlledCheckbox
              control={control}
              name='shouldUpdateProfile'
              label='Update my profile to reflect this information'
            />
          </Form.Field>
        )}
        {!!isApplicantField && !isStaffSubmission && <Divider />}
        {section.fields
          .filter((field) => !field.attributes.isApplicantField)
          .map((field) => (
            <FormField
              key={field.id}
              applicationForm={applicationForm}
              permitsFormField={field}
              errors={applicationForm.errors}
              fields={fields}
              isCitizen={isCitizen}
              {...props}
            />
          ))}
        <Divider hidden section />
        <Segment basic clearing style={{ padding: 0, marginTop: 0 }}>
          <Button
            className='permitsButton'
            disabled={!showFields}
            primary
            size='small'
            content={hasMore ? "Next" : "Submit"}
            floated='right'
          />
        </Segment>
      </Form>
    </div>
  );
};
